import BitmapTextField from "../views/components/BitmapTextField";
import TextField from "../views/components/TextField";
import Locale from "./Locale";
import { DisplayObject, Point, Rectangle, Sprite, Text } from "pixi.js";
import { Tween } from "../Tween";
import RequestRegister from "../server/requests/RequestRegister";
import { IRequestBuilder } from "../server/requests/RequestBuilder";
import CommManager from "../controllers/CommManager";
import Comm from "../controllers/BaseComm";
import IComm, { ICommRequest } from "../controllers/IComm";

export enum Direction {
    Top = "top",
    Bottom = "bottom",
    Left = "left",
    Right = "right",
}

class Utils {
    static anchorContainer(obj, anchor: Point) {
        obj.pivot.set(obj.width * anchor.x, obj.height * anchor.y);
        obj.x = obj.x + obj.width * anchor.x;
        obj.y = obj.y + obj.height * anchor.y;
    }

    static sortSymbols(
        s1: DisplayObject,
        s2: DisplayObject,
        sortDir: { x?: string; y?: string }
    ): number {
        let sortRes = 0;
        switch (sortDir.x) {
            case Direction.Left:
                switch (sortDir.y) {
                    case Direction.Top:
                        sortRes = Utils.sortByPositionLeftTop(s1, s2);
                        break;
                    default:
                        sortRes = Utils.sortByPositionLeftBottom(s1, s2);
                        break;
                }
                break;
            case Direction.Right:
                switch (sortDir.y) {
                    case Direction.Top:
                        sortRes = Utils.sortByPositionRightTop(s1, s2);
                        break;
                    default:
                        sortRes = Utils.sortByPositionRightBottom(s1, s2);
                        break;
                }
                break;
            default:
                sortRes = Utils.sortByPositionRightBottom(s1, s2);
                break;
        }

        return sortRes;
    }

    static sortByPositionRightBottom(
        s1: DisplayObject,
        s2: DisplayObject
    ) {
        let sortRes = 0;
        if (s1.x > s2.x) {
            sortRes = 1;
        } else if (s1.x < s2.x) {
            sortRes = -1;
        } else if (s1.y > s2.y) {
            sortRes = 1;
        } else if (s1.y < s2.y) {
            sortRes = -1;
        }

        return sortRes;
    }

    static sortByPositionLeftBottom(
        s1: DisplayObject,
        s2: DisplayObject
    ) {
        let sortRes = 0;
        if (s1.x < s2.x) {
            sortRes = 1;
        } else if (s1.x > s2.x) {
            sortRes = -1;
        } else if (s1.y > s2.y) {
            sortRes = 1;
        } else if (s1.y < s2.y) {
            sortRes = -1;
        }

        return sortRes;
    }

    static sortByPositionRightTop(
        s1: DisplayObject,
        s2: DisplayObject
    ) {
        let sortRes = 0;
        if (s1.x > s2.x) {
            sortRes = 1;
        } else if (s1.x < s2.x) {
            sortRes = -1;
        } else if (s1.y < s2.y) {
            sortRes = 1;
        } else if (s1.y > s2.y) {
            sortRes = -1;
        }

        return sortRes;
    }

    static sortByPositionLeftTop(
        s1: DisplayObject,
        s2: DisplayObject
    ) {
        let sortRes = 0;
        if (s1.x < s2.x) {
            sortRes = 1;
        } else if (s1.x > s2.x) {
            sortRes = -1;
        } else if (s1.y < s2.y) {
            sortRes = 1;
        } else if (s1.y > s2.y) {
            sortRes = -1;
        }

        return sortRes;
    }

    static parseStringifiedListOfNumbers(list: string): number[] {
        let result: number[] = [];

        let vals = list.split(",");
        vals.forEach((val) => {
            result.push(parseFloat(val));
        });
        return result;
    }

    static parseStringListToNumbersList(list: string[]): number[] {
        let result: number[] = [];

        list.forEach((val) => {
            let num = parseFloat(val);
            if (!isNaN(num)) {
                result.push(num);
            }
        });
        return result;
    }

    static addedToStage(display, callback, args = null, context = null) {
        console.error("MOVED TO APP.TS");
    }

    static calculateChance(percentChance: number): boolean {
        return Math.random() * 100 <= percentChance;
    }

    static spriteClone(obj: Sprite): Sprite {
        let s = new Sprite(obj.texture);
        s.position = obj.position;
        s.scale = obj.scale;
        s.rotation = obj.rotation;
        s.anchor = obj.anchor;
        s.pivot = obj.pivot;
        return s;
    }

    static deepClone(obj: any): any {
        if (!obj) {
            return obj;
        }

        if (obj instanceof Map) {
            return new Map(obj);
        }

        let clone = JSON.parse(JSON.stringify(obj));
        return clone;
    }

    static getRandomEl(arr: any[]): any {
        return arr[Math.floor(Math.random() * arr.length)];
    }

    static compareArrays(arr1: any[], arr2: any[]): boolean {
        if (arr1.length != arr2.length) {
            return false;
        }

        for (let i = 0; i < arr1.length; i++) {
            if (Array.isArray(arr1[i]) && Array.isArray(arr2[i])) {
                if (!Utils.compareArrays(arr1[i], arr2[i])) {
                    return false;
                }
            } else if (arr1[i] !== arr2[i]) {
                return false;
            }
        }

        return true;
    }

    static serialize(data, object) {
        if (!data) {
            return;
        }

        Object.keys(data).forEach((key) => {
            if (object.hasOwnProperty(key) && data[key] != undefined) {
                object[key] = data[key];
            }
        });
    }

    static fillTextField = (
        sceneTextField: TextField | BitmapTextField | Text,
        name: string,
        getStringModule: typeof Locale
    ): void => {
        sceneTextField.text = getStringModule.getString(name);
    };

    static fillTextFields = (
        sceneTextFields: Map<string, TextField | BitmapTextField | Text>,
        getStringModule: typeof Locale
    ): void => {
        sceneTextFields.forEach((field, name) => {
            Utils.fillTextField(field, name, getStringModule);
        });
    };

    static merge(dataObj, obj) {
        if (!dataObj || !obj) {
          return;
        }
    
        Object.keys(dataObj).forEach((key) => {
          if (dataObj[key] != undefined) {
            obj[key] = dataObj[key];
          }
        });
      }
    
    static truncateNumber(value: number, decimals: number) {
        let valueStr = value.toString();
        if (valueStr.indexOf(".") != -1) {
            valueStr = valueStr.slice(0, (valueStr.indexOf(".")) + decimals + 1);
        }

        return Number(valueStr);
    }

    static isNullOrWhiteSpace(str: string): boolean {
        if (!str) {
            return true;
        }

        return str.match(/^ *$/) !== null;
    }

    static getDeviceInfo() {
        // We are using require because of the unit tests
        const du = require("device-uuid");
        const uuid = new du.DeviceUUID();
        const userAgent = window.navigator.userAgent;
        return {
            "deviceId": uuid.get(),
            "browser": uuid.getBrowser(userAgent),
            "OS": uuid.getOS(userAgent),
            "platform": uuid.getPlatform(userAgent)
        }
    }

    static async wait(timeInSeconds: number) {
        return new Promise<void>(resolve => {
            Tween.delayedCall(timeInSeconds, resolve);
        })
    }

    static almostEquals(a: number, b: number, precision: number = 1e-9) {
        return Math.abs(a - b) < precision;
    }

    static getPositionAlongTheLine(x1: number, y1: number, x2: number, y2: number, percentage: number) {
        let x = x1 * (1.0 - percentage) + x2 * percentage;
        let y = y1 * (1.0 - percentage) + y2 * percentage;
        return new Point(x, y);
    }

    static groupBy<T>(array: T[], key: string) {
        return array.reduce((p, c) => {
            (p[c[key]] = p[c[key]] || []).push(c);
            return p;
          }, {});
    }

    static matrixInclues<T>(matrix: T[][], value: T) {
        if (!matrix) {
            return false;
        }
        
        return matrix.some(array => array.some(val => val === value));
    }

    /**
     * Computes the composite axis alined bounding box of the passed rectangles.
     * @param rects The rects, whose composite bound to compute.
     * @returns The composite AABB of the passed rects.
     */
         public static createCompositeBound(
            rects: Array<Rectangle>,
            scalar: number
        ): Rectangle {
            let composite: Rectangle;
            for (let i = 0; i < rects.length; i++) {
                const rect = rects[i];
                Utils.scaleBounds(rect, scalar);
                if (composite) {
                    composite.enlarge(rect);
                } else {
                    composite = rect;
                }
            }
    
            return composite;
        }

    /**
     * Scales rectangle from its center by provided multiplier
     * @param bound the bound to scale
     * @param scalar multiplier
     */
    public static scaleBounds(
        bound: Rectangle,
        scalarX: number,
        scalarY: number = null
    ) {
        if (!scalarY) {
            bound.width *= scalarX;
            bound.height *= scalarX;

            let xDifference = (bound.width - bound.width / scalarX) / 2;
            let yDifference = (bound.height - bound.height / scalarX) / 2;
            bound.x -= xDifference;
            bound.y -= yDifference;
            return;
        }

        bound.width *= scalarX;
        bound.height *= scalarY;

        let xDifference = (bound.width - bound.width / scalarX) / 2;
        let yDifference = (bound.height - bound.height / scalarY) / 2;
        bound.x -= xDifference;
        bound.y -= yDifference;
    }

    /**
     * Checks whether we are in iFrame or not.
     *
     * @static
     * @return {*} 
     * @memberof Utils
     */
    static inIframe () {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }

    /**
     * Checks whether a value is a numeric value or a string representation of a number.
     * @param {any} value - The value to be checked.
     * @returns {boolean} Returns true if the value is a valid finite number, otherwise returns false.
     */
    static isNumeric(value: any): boolean {
        return Number.isFinite(Number(value));
    }

    /**
     * Sends a custom error to the backend, in case the game has 'sendError' request already setup.
     * Usually the sendError is setup the following way:
     * ```js
     * RequestRegister.add("sendError", new SendErrorRequest());
     * ```
     *
     * @static
     * @param {string} errorMessage The error message to send to the error endpoint. Can not be null or white-space.
     * @param {*} dataToSend The data to send to the error endpoint. Can not be null.
     * @param {IComm} comm The comm, used to send the error. Usually retrieved by calling 'CommManager.getComm();'
     * @memberof Utils
     */
    static sendCustomError(errorMessage: string, dataToSend: any, comm: IComm) {
        if (Utils.isNullOrWhiteSpace(errorMessage)) {
            throw new Error("errorMessage can not be null or white-space");
        }

        if (!dataToSend) {
            throw new Error("data can not be null or undefined");
        }

        if (!comm) {
            throw new Error("comm can not be null or undefined");
        }

        if (RequestRegister.has("sendError")) {
            const originalBuilder = RequestRegister.get("sendError");
            const mockedBuilder: IRequestBuilder = {
                path: originalBuilder.path,
                method: originalBuilder.method,
                requestData: {
                    error: errorMessage,
                    customData: dataToSend
                }
            };

            const errorRequest: ICommRequest = {
                builder: mockedBuilder,
                callback: () => {},
                callbackScope: this,
                errorCallback: undefined,
                withCredentials: false,
                enqueueRequest: false,
                retryCount: 0
            };

            comm.send(errorRequest);
        }
    }
}

export default Utils;
